/**
 * Where a break should go inside a shift.
 *
 * Working-time law wants the break part-way through the work, not tacked onto
 * either end, so the planner aims for the middle of the shift and keeps clear
 * of the first and last hour where it can. It also steps around breaks other
 * guards on the same post already take, so the post is not left empty.
 */

import { BREAK_DAY_END_MINUTE, ROSTER_SNAP_MINUTES, overlaps, snapDown, snapUp } from './rosterModel';
import type { TimeWindow } from './types';

export interface BreakPlanRequest {
  shift: TimeWindow;
  durationMinutes: number;
  /** Windows to keep clear: the guard's other breaks and colleagues' breaks on the same duty. */
  avoid?: TimeWindow[];
  /** Where the break would ideally start, e.g. where a short break already sits. */
  preferredStartMinute?: number | null;
}

const EDGE_MARGIN_MINUTES = 60;

export function planBreakWindow(request: BreakPlanRequest): TimeWindow | null {
  const { shift, durationMinutes } = request;
  if (durationMinutes <= 0) return null;

  const earliestStart = shift.startMinute;
  // A break cannot be booked past midnight of the roster day.
  const latestEnd = Math.min(shift.endMinute, BREAK_DAY_END_MINUTE);
  const room = latestEnd - earliestStart - durationMinutes;
  if (room < 0) return null;

  const margin = Math.min(EDGE_MARGIN_MINUTES, Math.floor(room / 2));
  const lowest = earliestStart + margin;
  const highest = latestEnd - margin - durationMinutes;

  const midpointStart = ((shift.startMinute + shift.endMinute) / 2) - (durationMinutes / 2);
  const preferred = request.preferredStartMinute;
  const ideal = preferred !== null && preferred !== undefined && preferred >= lowest && preferred <= highest
    ? preferred
    : midpointStart;

  const starts: number[] = [];
  for (let start = snapUp(lowest); start <= snapDown(highest); start += ROSTER_SNAP_MINUTES) {
    starts.push(start);
  }
  if (starts.length === 0) starts.push(lowest);
  if (preferred !== null && preferred !== undefined && preferred >= lowest && preferred <= highest && !starts.includes(preferred)) {
    starts.push(preferred);
  }

  starts.sort((a, b) => Math.abs(a - ideal) - Math.abs(b - ideal) || a - b);

  const avoid = request.avoid ?? [];
  const toWindow = (start: number): TimeWindow => ({ startMinute: start, endMinute: start + durationMinutes });
  const clear = starts.find((start) => !avoid.some((window) => overlaps(toWindow(start), window)));

  return toWindow(clear ?? starts[0]);
}
